<template>
  <div class="full-screen-background">
    <div class="wiki-itens">

      <h1 class="text-center">Digite o nome do item que deseja buscar</h1><br>
      <input
        v-model="rawSearch"
        @input="onInput"
        :placeholder="t('wiki.searchPlaceholder')"
        class="search-bar"
      />

      <ul
        class="item-list"
        ref="list"
        @scroll.passive="onScroll"
      >
        <li
          v-for="item in displayedItems"
          :key="item.id + '-' + item.icon_id"
          class="item"
          @click="openModal(item)"
        >
          <img
            v-if="item.icon_id"
            :src="`/auxiliadores/itemicon/${item.icon_id}.png`"
            class="icon"
            :alt="t('wiki.itemIconAlt', { name: item.nome })"
          />
          <span>{{ item.id }} – {{ item.nome }}</span>
        </li>
        <li v-if="loadingMore" class="loading">
          {{ t('wiki.loading') }}
        </li>
      </ul>

      <!-- Modal principal -->
      <Teleport to="body">
        <div
          v-if="activeItem"
          class="modal-backdrop"
          @click.self="activeItem = null"
        >
          <div class="modal gameOpacity">
            <h2>{{ activeItem.nome }}</h2>
            <p class="description">{{ activeItem.descricaoCompleta }}</p>

            <div class="modal-content">
              <!-- Coluna esquerda -->
              <div class="modal-left">
                <div class="section" style="margin-top:16px;">
                  <h3>{{ t('wiki.setItemsTitle') }}</h3>
                  <ul v-if="setItems.length" class="set-items">
                    <li v-for="si in setItems" :key="si.id" class="set-item">
                      <img
                        v-if="si.icon_id"
                        :src="`/auxiliadores/itemicon/${si.icon_id}.png`"
                        class="icon"
                        :alt="t('wiki.itemIconAlt', { name: si.nome })"
                      />
                      <span>{{ si.id }} – {{ si.nome }}</span>
                    </li>
                  </ul>
                  <p v-else class="empty-message">Informações não encontradas.</p>
                </div>
                <v-divider :thickness="17" color="error"></v-divider>
                <div class="section">
                  <h3>{{ t('wiki.setBonusesTitle') }}</h3>
                  <ul v-if="setBonuses.length" class="set-bonuses">
                    <li
                      v-for="(b, idx) in setBonuses"
                      :key="idx"
                      class="bonus-item"
                    >
                      {{ b }}
                    </li>
                  </ul>
                  <p v-else class="empty-message">Informações não existem.</p>
                </div>
              </div>

              <div class="vertical-divider"></div>

              <!-- Coluna direita -->
              <div class="modal-right">
                <div class="section" style="margin-top:16px;">
                  <h3>{{ t('wiki.recipeTitle') }}</h3>
                  <ul v-if="recipeItems.length" class="recipe-list">
                    <li
                      v-for="ri in recipeItems"
                      :key="ri.id"
                      class="recipe-item"
                    >
                      <img
                        v-if="ri.icon_id"
                        :src="`/auxiliadores/itemicon/${ri.icon_id}.png`"
                        class="icon"
                        :alt="t('wiki.itemIconAlt', { name: ri.nome })"
                      />
                      <div class="recipe-info">
                        <span>{{ ri.id }} – {{ ri.nome }}</span>
                        <small>{{
                          t('wiki.recipeQty', { qty: ri.qty })
                        }}</small>
                      </div>
                    </li>
                  </ul>
                  <p v-else class="empty-message">Informações não encontradas.</p>
                </div>
                <v-divider :thickness="17" color="error"></v-divider>
                <div class="section">
                  <h3>{{ t('wiki.obtainTitle') }}</h3>
                  <p v-if="obtainDesc">{{ obtainDesc }}</p>
                  <p v-else class="empty-message">Informações não existem.</p>
                </div>
              </div>
            </div>

            <div class="modal-actions">
              <button @click="activeItem = null">
                {{ t('wiki.closeButton') }}
              </button>
              <button class="secondary" @click="openOndeEncontrar()">
                Onde Encontrar
              </button>
            </div>
          </div>
        </div>
      </Teleport>

      <!-- Modal “Onde Encontrar” -->
      <Teleport to="body">
        <div
          v-if="showOndeEncontrar"
          class="modal-backdrop"
          @click.self="closeOndeEncontrar()"
        >
          <div class="modal gameOpacity onde-encontrar-modal">
            <h2>Onde Encontrar — {{ activeItem.nome }}</h2>
            <div class="onde-encontrar-content">
              <div class="sources-list">
                <h3>Fontes de Item</h3>
                <ul v-if="sourcesItens.length">
                  <li
                    v-for="src in sourcesItens"
                    :key="src.id"
                    class="source-item"
                  >
                    <img
                      v-if="src.icon_id"
                      :src="`/auxiliadores/itemicon/${src.icon_id}.png`"
                      class="icon"
                      :alt="src.nome"
                    />
                    <span>{{ src.id }} – {{ src.nome }}</span>
                  </li>
                </ul>
                <p v-else class="empty-message">Nenhuma fonte de item encontrada.</p>

                <v-divider
                  v-if="sourcesItens.length && sourcesMonstros.length"
                  color="error"
                ></v-divider>

                <h3>Fontes de Monstros</h3>
                <ul v-if="sourcesMonstros.length">
                  <li
                    v-for="src in sourcesMonstros"
                    :key="src.id"
                    class="source-item monster-source"
                    @click="selectMonster(src)"
                  >
                    <img
                      v-if="src.icon_id"
                      :src="`/auxiliadores/itemicon/${src.icon_id}.png`"
                      class="icon"
                      :alt="src.nome"
                    />
                    <span>{{ src.id }} – {{ src.nome }}</span>
                  </li>
                </ul>
                <p v-else class="empty-message">Nenhum monstro encontrado como fonte.</p>
              </div>

              <transition name="slide">
                <div
                  v-if="selectedMonster"
                  class="monster-status-panel"
                >
                  <h3>Status do Monstro</h3>
                  <p><strong>Nome:</strong> {{ selectedMonster.nome }}</p>
                  <p><strong>Health Points:</strong> {{ selectedMonster.healthPoints }}</p>
                  <p><strong>Stamina Points:</strong> {{ selectedMonster.staminaPoints }}</p>
                  <v-divider color="error"></v-divider>
                  <div class="skills-list">
                    <template
                      v-for="(skill, idx) in selectedMonster.skills"
                      :key="skill.id"
                    >
                      <v-divider v-if="idx > 0" color="error"></v-divider>
                      <div class="skill-detail">
                        <p><strong>{{ skill.id }} – {{ skill.name }}</strong></p>
                        <p>Custo: {{ skill.cost }} MP</p>
                        <p>Conjuração: {{ skill.castTime }} segundos</p>
                        <p>Recarga: {{ skill.cooldown }} segundos</p>
                        <!-- só aparece se houver descrição válida -->
                        <p v-if="skill.description">{{ skill.description }}</p>
                      </div>
                    </template>
                  </div>
                </div>
              </transition>
            </div>

            <button @click="closeOndeEncontrar()">
              {{ t('wiki.closeButton') }}
            </button>
          </div>
        </div>
      </Teleport>

    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import debounce from 'lodash/debounce'

const { t, locale } = useI18n()

// constantes de paginação
const ITEMS_PER_PAGE   = 200
const SCROLL_THRESHOLD = 50

// estados reativos
const rawSearch         = ref('')
const searchText        = ref('')
const itens             = ref([])
const itensFiltrados    = ref([])
const page              = ref(1)
const loadingMore       = ref(false)
const activeItem        = ref(null)
const showOndeEncontrar = ref(false)
const selectedMonster   = ref(null)
const list              = ref(null)

// data auxiliares
const equipSets   = ref([])
const tradMap     = ref({})
const collMap     = ref({})
const elfCombines = ref([])

// arquivos INI externos
const dropLines     = ref([])
const monsterLines  = ref([])
const tMonsterLines = ref([])
const spellsMap     = ref({})

// função de tradução on-the-fly
async function translateMap(map, targetLang) {
  const entries = Object.entries(map)
  const names   = entries.map(([, [nome]]) => nome)
  const descs   = entries.map(([, [, desc]]) => desc)
  const all     = [...names, ...descs]
  const res     = await fetch('https://libretranslate.de/translate', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ q: all, source: 'pt', target: targetLang, format: 'text' })
  })
  const json = await res.json()
  if (!Array.isArray(json)) return map
  const tNames = json.slice(0, names.length).map(r => r.translatedText)
  const tDescs = json.slice(names.length).map(r => r.translatedText)
  const out    = {}
  entries.forEach(([id], i) => {
    out[id] = [ tNames[i] || names[i], tDescs[i] || descs[i] ]
  })
  return out
}

// carrega um arquivo como string
async function loadText(path) {
  const res = await fetch(path)
  const buf = await res.arrayBuffer()
  return new TextDecoder('windows-1252').decode(buf)
}

// filtra linhas sem comentários
function parseIniLines(text) {
  return text.split('\n')
    .map(l => l.trim())
    .filter(l => l && !l.startsWith(';'))
}

// parse de itens C_Item e C_ItemMall
function parseItens(lines, trad, ench) {
  const out = []
  for (const l of lines) {
    const p = l.split('|')
    if (p.length <= 69) continue
    const id      = p[0]
    const icon    = p[1]
    if (!icon) continue
    const enchId  = p[69] || ''
    const [nome, rawDesc] = trad[id] || [`Item ${id}`, '[Sem descrição]']
    const [enChNome = '', rawEfeito = ''] = ench[enchId] || ['', '']
    const desc   = rawDesc.replace(/\$(?:35|7|12|15)\$/g, '').trim()
    const efeito = rawEfeito.replace(/\$(?:35|7|12|15)\$/g, '').trim()
    const fullDesc = efeito
      ? `${desc}\n\n<${enChNome}>\n${efeito}`
      : desc
    out.push({ id, icon_id: icon, nome, descricaoCompleta: fullDesc })
  }
  return out
}

// parse de conjuntos
function parseEquipSets(lines) {
  return lines.map(l => {
    const p = l.split('|')
    return {
      itemIds:  p.slice(4, 9).filter(x => x),
      bonusIds: p.slice(14,17).filter(x => x)
    }
  })
}

// parse de combinações de elfos
function parseElfCombine(lines) {
  return lines.map(l => {
    const p = l.split('|')
    return {
      mainId: p[10],
      recipe: [
        { id: p[12], qty: p[14] },
        { id: p[15], qty: p[17] },
        { id: p[18], qty: p[20] }
      ].filter(r => r.id && r.qty)
    }
  })
}

// carrega mapeamentos T_*
async function loadMap(path) {
  const text = await loadText(path)
  const m    = {}
  for (const l of parseIniLines(text)) {
    const p = l.split('|')
    if (p.length >= 3) {
      m[p[0]] = [
        p[1],
        p[2].replace(/\$(?:35|7|12|15)\$/g, '').trim()
      ]
    }
  }
  return m
}

// coleção
async function loadCollectionMap(path) {
  const text = await loadText(path)
  const m    = {}
  for (const l of parseIniLines(text)) {
    const p = l.split('|')
    if (p.length >= 2) {
      m[p[0]] = p[1].replace(/\$(?:35|7|12|15)\$/g, '').trim()
    }
  }
  return m
}

// parser de T_Spell.ini com correção para entradas sem descrição
function parseSpells(lines) {
  const map = {}
  for (let i = 0; i < lines.length; i++) {
    const l = lines[i]
    const m = l.match(/^(\d+)\|([^|]*)\|/)
    if (m) {
      const id   = m[1]
      const name = m[2] || ''
      let cost = '', castTime = '', cooldown = ''
      // se for do tipo "12345|Nome||", sem linhas adicionais, ignora leitura de linhas seguintes
      const isSimpleEntry = /^\d+\|[^|]*\|\|$/.test(l)
      if (isSimpleEntry) {
        map[id] = { id, name, cost, castTime, cooldown, description: '' }
        continue
      }
      const descriptionParts = []
      for (let j = i + 1; j < lines.length; j++) {
        const line = lines[j]
        const mc   = line.match(/Custo:\s*#(\d+)#/)
        if (mc) cost = mc[1]
        const m2   = line.match(/Conjuração:\s*#(\d+)#/)
        if (m2) castTime = m2[1]
        const m3   = line.match(/Recarga:\s*#(\d+)#/)
        if (m3) cooldown = m3[1]
        descriptionParts.push(
          line.replace(/#(\d+)#/g, '$1').replace(/\|$/, '').trim()
        )
        if (line.endsWith('|')) {
          i = j
          break
        }
      }
      const description = descriptionParts.join(' ')
      map[id] = { id, name, cost, castTime, cooldown, description }
    }
  }
  return map
}

onMounted(async () => {
  // 1) Itens e tradução
  const [t1, t2, ench] = await Promise.all([
    loadMap('/auxiliadores/T_Item.ini'),
    loadMap('/auxiliadores/T_ItemMall.ini'),
    loadMap('/auxiliadores/T_Enchant.ini')
  ])
  tradMap.value = { ...t1, ...t2 }
  if (locale.value !== 'pt') {
    tradMap.value = await translateMap(tradMap.value, locale.value)
  }

  // 2) Carrega itens
  const [c1, c2] = await Promise.all([
    loadText('/auxiliadores/C_Item.ini'),
    loadText('/auxiliadores/C_ItemMall.ini')
  ])
  itens.value = [
    ...parseItens(parseIniLines(c1), tradMap.value, ench),
    ...parseItens(parseIniLines(c2), tradMap.value, ench)
  ]
  filterAndReset()

  // 3) Conjuntos, coleções e combinações
  const rawEquip = await loadText('/auxiliadores/C_EquipSet.ini')
  equipSets.value = parseEquipSets(parseIniLines(rawEquip))
  collMap.value   = await loadCollectionMap('/auxiliadores/T_Collection.ini')
  const rawElf    = await loadText('/auxiliadores/C_ElfCombine.ini')
  elfCombines.value = parseElfCombine(parseIniLines(rawElf))

  // 4) C_DropItem.ini
  const rawDrop = await loadText('/auxiliadores/C_DropItem.ini')
  dropLines.value = parseIniLines(rawDrop)

  // 5) C_Monster.ini (combina registros divididos)
  const rawMC   = await loadText('/auxiliadores/C_Monster.ini')
  const linesMC = rawMC.split('\n')
    .map(l => l.trim())
    .filter(l => l && !l.startsWith(';'))
  const recs    = []
  for (let i = 0; i < linesMC.length; i++) {
    let l = linesMC[i]
    if (/^\d+\|/.test(l)) {
      const p = l.split('|')
      if (p.length < 50 && linesMC[i+1] && !/^\d+\|/.test(linesMC[i+1])) {
        l += '|' + linesMC[i+1]
        i++
      }
      recs.push(l)
    }
  }
  monsterLines.value = recs

  // 6) T_Monster.ini (descrições)
  const rawTM   = await loadText('/auxiliadores/T_Monster.ini')
  const linesTM = rawTM.split('\n')
  for (let i = 0; i < linesTM.length; i++) {
    const l = linesTM[i].trim()
    if (/^\d+\|/.test(l)) {
      const p    = l.split('|')
      const id   = p[0]
      let desc   = p[1] || ''
      if (p.length < 3 && linesTM[i+1]) {
        desc += ' ' + linesTM[i+1].split('|')[0].trim()
      }
      tMonsterLines.value.push({ id, desc: desc.trim() })
    }
  }

  // 7) T_Spell.ini
  const rawSpell   = await loadText('/auxiliadores/T_Spell.ini')
  const spellLines = rawSpell.split('\n').map(l => l.trim())
  spellsMap.value  = parseSpells(spellLines)
})

// filtragem e paginação
const onInput = debounce(() => filterAndReset(), 300)
function filterAndReset() {
  const t0 = rawSearch.value.trim().toLowerCase()
  searchText.value = t0
  itensFiltrados.value = t0
    ? itens.value.filter(i =>
        i.nome.toLowerCase().includes(t0) || i.id.includes(t0)
      )
    : []
  page.value = 1
}
const displayedItems = computed(() =>
  itensFiltrados.value.slice(0, page.value * ITEMS_PER_PAGE)
)
function onScroll() {
  const el = list.value
  if (!el || loadingMore.value) return
  if (el.scrollHeight - el.scrollTop - el.clientHeight < SCROLL_THRESHOLD) {
    loadingMore.value = true
    setTimeout(() => {
      page.value++
      loadingMore.value = false
    }, 100)
  }
}

// modais
function openModal(item) {
  activeItem.value = item
}
function openOndeEncontrar() {
  showOndeEncontrar.value = true
  selectedMonster.value = null
}
function closeOndeEncontrar() {
  showOndeEncontrar.value = false
  selectedMonster.value = null
}
function selectMonster(src) {
  selectedMonster.value = src
}

// computeds originais
const activeSet = computed(() =>
  activeItem.value
    ? equipSets.value.find(s => s.itemIds.includes(activeItem.value.id))
    : null
)
const setItems = computed(() =>
  activeSet.value
    ? activeSet.value.itemIds
        .map(id => itens.value.find(i => i.id === id))
        .filter(Boolean)
    : []
)
const setBonuses = computed(() =>
  activeSet.value
    ? activeSet.value.bonusIds
        .map(id => (tradMap.value[id]?.[1] || '').replace(/º/g, ''))
        .filter(Boolean)
    : []
)
const obtainDesc = computed(() =>
  activeItem.value ? collMap.value[activeItem.value.id] || '' : ''
)
const recipeItems = computed(() => {
  if (!activeItem.value) return []
  const entry = elfCombines.value.find(e => e.mainId === activeItem.value.id)
  return entry
    ? entry.recipe.map(r => {
        const info = itens.value.find(i => i.id === r.id)
        return {
          id:      r.id,
          nome:    info?.nome || tradMap.value[r.id]?.[0] || `Item ${r.id}`,
          icon_id: info?.icon_id || '',
          qty:     r.qty
        }
      })
    : []
})

// Onde Encontrar com HP, SP e spells corretos
const ondeEncontrarItems = computed(() => {
  if (!activeItem.value) return []
  const idAlvo = activeItem.value.id
  const list   = []

  for (const line of dropLines.value) {
    const parts   = line.split('|')
    const payload = parts.slice(12).filter(x => x)
    if (!payload.includes(idAlvo)) continue
    const sourceId = parts[0]
    // item
    let itm = itens.value.find(i => i.id === sourceId)
    if (itm) {
      list.push({ ...itm, type: 'item' })
      continue
    }
    if (tradMap.value[sourceId]) {
      const [nome, desc] = tradMap.value[sourceId]
      list.push({ id: sourceId, nome, descricaoCompleta: desc, icon_id: '', type: 'item' })
      continue
    }
    // monstro
    const recM = monsterLines.value.find(r => r.startsWith(sourceId + '|'))
    if (recM) {
      const p       = recM.split('|')
      const hp      = p[8]   || '0'
      const mp      = p[9]   || '0'
      // skills corretos nos índices 47,48,49
      const skillIds= p.slice(47,50).filter(x => x && /^\d+$/.test(x))
      const tmon    = tMonsterLines.value.find(m => m.id === sourceId)
      const nome    = tmon?.desc || p[2] || `Monstro ${sourceId}`
      const skillObjs = skillIds
        .map(id => spellsMap.value[id])
        .filter(Boolean)
      list.push({
        id: sourceId,
        nome,
        descricaoCompleta: nome,
        icon_id: '',
        type: 'monster',
        healthPoints: hp,
        staminaPoints: mp,
        skills: skillObjs
      })
    }
  }

  return list
})

const sourcesItens = computed(() =>
  ondeEncontrarItems.value.filter(src => src.type === 'item')
)
const sourcesMonstros = computed(() =>
  ondeEncontrarItems.value.filter(src => src.type === 'monster')
)
</script>

<style lang="scss" scoped>
.full-screen-background {
  background-image: url('@/assets/background/fundotrocasenha.png');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  width: 100%;
  position: relative;
}

.wiki-itens {
  color: #fff;
  padding: 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.search-bar {
  width: 80%; max-width: 500px; padding: 8px; margin-bottom: 8px;
  background: #1e1e1e; border: none; color: #fff;
}

.item-list {
  width: 80%; max-width: 500px; height: 80vh;
  overflow-y: auto; list-style: none; margin: 0; padding: 0;
}

.item {
  display: flex; align-items: center; padding: 4px;
  height: 56px; font-size: 14px; cursor: pointer;
}
.item + .item { border-top: 1px solid #333; }

.icon {
  width: 48px; height: 48px; margin-right: 8px; object-fit: contain;
}

.loading {
  padding: 8px; text-align: center; color: #999;
}

.modal-backdrop {
  position: fixed; inset: 0;
  background: rgba(0,0,0,0.6);
  display: flex; align-items: center; justify-content: center;
}

.modal {
  background-color: rgba(0,0,0,0.63);
  padding: 16px; border-radius: 4px;
  width: 80%; max-width: 700px; max-height: 90vh; overflow-y: auto;
}

.description { white-space: pre-wrap; margin-bottom: 16px; }

.modal-content { display: flex; gap: 16px; }
.modal-left, .modal-right { flex: 1; }
.vertical-divider { width: 1px; background: #444; margin: 0 8px; }

.section h3 { margin-bottom: 8px; }
.set-items, .recipe-list, .set-bonuses {
  list-style: none; padding: 0; margin: 8px 0;
}
.set-item, .recipe-item {
  display: flex; align-items: center; margin-bottom: 6px;
}
.set-item .icon, .recipe-item .icon {
  width: 32px; height: 32px; margin-right: 8px;
}
.recipe-info small {
  display: block; font-size: 12px; color: #ccc;
}
.bonus-item {
  padding: 8px 0; border-bottom: 1px solid #444;
}
.bonus-item:last-child { border-bottom: none; }

.empty-message {
  font-style: italic; color: #bbb; padding: 8px 0;
}

.modal-actions {
  display: flex; justify-content: flex-end; gap: 8px; margin-top: 16px;
}
.modal-actions .secondary { background: #555; }
button {
  padding: 6px 12px; background: #333; color: #fff;
  border: none; cursor: pointer;
}

/* Onde Encontrar Modal */
.onde-encontrar-modal { max-width: 800px; }
.onde-encontrar-content { display: flex; gap: 16px; }
.sources-list { flex: 1; }
.monster-status-panel {
  flex: 1; padding: 16px; background: rgba(255,255,255,0.1);
  border-radius: 4px;
}
.source-item {
  display: flex; align-items: center; margin-bottom: 8px;
  cursor: pointer;
}
.source-item .icon {
  width: 32px; height: 32px; margin-right: 8px;
}
.skills-list {
  margin-top: 8px;
}
.skill-detail {
  margin: 8px 0;
}

/* slide transition */
.slide-enter-active, .slide-leave-active {
  transition: all 0.3s ease;
}
.slide-enter-from {
  transform: translateX(100%); opacity: 0;
}
.slide-enter-to {
  transform: translateX(0); opacity: 1;
}
.slide-leave-from {
  transform: translateX(0); opacity: 1;
}
.slide-leave-to {
  transform: translateX(100%); opacity: 0;
}
</style>
